import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, Send, Stethoscope, Ambulance, FlaskConical, Pill, ArrowRight, ShieldAlert, Volume2, VolumeX } from "lucide-react";
import MicButton from "@/components/MicButton";

const URGENCY_STYLE = {
  emergency: "bg-red-100 text-red-800 border-red-200",
  urgent: "bg-amber-100 text-amber-900 border-amber-200",
  routine: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const ACTION_ICON = {
  emergency_ambulance: Ambulance,
  book_opd: Stethoscope,
  book_lab: FlaskConical,
  self_care_pharmacy: Pill,
};

export default function AITriage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sid, setSid] = useState(null);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi, I'm Aadhya — MedHOne AI Care. How can I help you today? You can type or tap the mic to speak. For example: my father has chest pain since 20 minutes, or my child has a fever of 102°F for 2 days." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [rec, setRec] = useState(null);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("aadhya_voice") !== "off");
  const scrollRef = useRef(null);
  const audioRef = useRef(null);

  const speak = async (text) => {
    if (!voiceOn || !text) return;
    try {
      const token = localStorage.getItem("medhone_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tts/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text, voice: "nova" }),
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (audioRef.current) { try { audioRef.current.pause(); } catch {} }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.play().catch(() => {});
    } catch (err) { console.error("Aadhya TTS failed", err); }
  };

  useEffect(() => {
    if (voiceOn) speak(messages[0].text);
    return () => { try { audioRef.current?.pause(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user === null) nav("/login");
  }, [user, nav]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, rec]);

  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    localStorage.setItem("aadhya_voice", next ? "on" : "off");
    if (!next && audioRef.current) { try { audioRef.current.pause(); } catch {} }
    toast.success(next ? "Aadhya's voice is on" : "Aadhya's voice is muted");
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/triage", { session_id: sid, message: text });
      setSid(data.session_id);
      if (data.kind === "question") {
        setMessages((m) => [...m, { role: "assistant", text: data.text }]);
        speak(data.text);
      } else {
        setRec(data.recommendation);
        const line = data.recommendation?.explanation || "Here's my recommendation.";
        setMessages((m) => [...m, { role: "assistant", text: line }]);
        speak(line + (data.recommendation?.cta_text ? ". " + data.recommendation.cta_text : ""));
      }
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  const takeAction = () => {
    if (!rec) return;
    if (rec.action === "emergency_ambulance") return nav("/emergency");
    if (rec.action === "book_opd") return nav("/book-opd");
    if (rec.action === "book_lab") return nav("/lab-tests");
    if (rec.action === "self_care_pharmacy") return nav("/pharmacy");
  };

  const startOver = () => {
    setSid(null); setRec(null); setInput("");
    setMessages([{ role: "assistant", text: "Let's start fresh. Please describe the situation." }]);
  };

  const Icon = rec && ACTION_ICON[rec.action] ? ACTION_ICON[rec.action] : Stethoscope;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-600 to-teal-500 flex items-center justify-center text-white shadow-lg shadow-teal-500/25">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="text-xs font-semibold uppercase tracking-widest text-teal-700">MedHOne AI Care · Aadhya</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">How can I help you today?</h1>
        </div>
        <Button
          data-testid="voice-toggle"
          variant="outline"
          size="sm"
          onClick={toggleVoice}
          className={voiceOn ? "border-teal-500 text-teal-700" : "text-slate-500"}
          title={voiceOn ? "Mute Aadhya's voice" : "Unmute Aadhya's voice"}
        >
          {voiceOn ? <Volume2 className="w-4 h-4 mr-1" /> : <VolumeX className="w-4 h-4 mr-1" />}
          {voiceOn ? "Voice on" : "Voice off"}
        </Button>
      </div>

      <Card className="rounded-2xl overflow-hidden">
        <div ref={scrollRef} className="p-4 sm:p-6 space-y-3 max-h-[55vh] min-h-[380px] overflow-y-auto bg-slate-50" data-testid="triage-thread">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-teal-600 text-white rounded-br-sm"
                  : "bg-white border border-slate-200 rounded-bl-sm"
              }`} data-testid={`msg-${m.role}-${i}`}>
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="max-w-[80%] px-4 py-2.5 rounded-2xl text-sm bg-white border border-slate-200 rounded-bl-sm">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400 mr-1 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400 mr-1 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}

          {rec && (
            <div className="pt-4 fade-up">
              <Card className={`p-5 rounded-2xl border-2 ${rec.urgency === "emergency" ? "border-red-300 bg-red-50" : rec.urgency === "urgent" ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`} data-testid="triage-recommendation">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${rec.urgency === "emergency" ? "bg-red-600 text-white" : "bg-slate-900 text-white"}`}>
                    {rec.urgency === "emergency" ? <ShieldAlert className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <div className="flex-1">
                    <Badge className={`mb-2 uppercase tracking-widest text-[10px] border ${URGENCY_STYLE[rec.urgency] || URGENCY_STYLE.routine}`}>{rec.urgency}</Badge>
                    <div className="font-display font-bold text-lg">{rec.cta_text || "Recommended next step"}</div>
                    <p className="text-sm text-slate-700 mt-1">{rec.explanation}</p>
                    {rec.specialization && (
                      <div className="mt-2 text-xs text-slate-700"><strong>Doctor:</strong> {rec.specialization}</div>
                    )}
                    {rec.tests?.length > 0 && (
                      <div className="mt-1 text-xs text-slate-700"><strong>Tests:</strong> {rec.tests.join(", ")}</div>
                    )}
                    {rec.ambulance_type && (
                      <div className="mt-1 text-xs text-slate-700"><strong>Ambulance:</strong> {rec.ambulance_type.toUpperCase()}</div>
                    )}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button onClick={takeAction} className={rec.urgency === "emergency" ? "bg-red-600 hover:bg-red-500 text-white" : "bg-teal-600 hover:bg-teal-500 text-white"} data-testid="triage-cta">
                        {rec.cta_text || "Continue"} <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                      <Button variant="outline" onClick={startOver} data-testid="triage-restart">Start over</Button>
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-[10px] text-slate-500 uppercase tracking-widest">
                  AI guidance only — not a medical diagnosis. Consult a qualified professional.
                </div>
              </Card>
            </div>
          )}
        </div>

        {!rec && (
          <div className="p-3 sm:p-4 border-t bg-white flex gap-2">
            <Input
              data-testid="triage-input"
              placeholder="Type or tap the mic to speak…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              disabled={loading}
            />
            <MicButton
              disabled={loading}
              onTranscript={(t) => { if (t) { setInput(t); toast.success("Voice transcribed — tap send"); } }}
            />
            <Button data-testid="triage-send" onClick={send} disabled={loading || !input.trim()} className="bg-teal-600 hover:bg-teal-500 text-white">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
